import { useAuth } from '@/context/auth-context'
import { Navigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useCallback, useState } from 'react'
import EmailVerificationOverlay from './EmailVerificationOverlay'
import { useVerificationPolling } from '@/hooks/use-verification-polling'
import { useToast } from '@/hooks/use-toast'

interface ProtectedRouteProps {
  children: React.ReactNode
  requireVerification?: boolean // Default: true
}

export default function ProtectedRoute({ 
  children, 
  requireVerification = true 
}: ProtectedRouteProps) {
  const { user, isLoading, logout } = useAuth()
  const { toast } = useToast()
  const [isVerified, setIsVerified] = useState<boolean | null>(null)
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false)

  // Handle verification completion
  const handleVerificationComplete = useCallback(() => {
    // First show the success animation
    setShowSuccessAnimation(true)
    
    toast({
      title: "Email verified! 🎉",
      description: "Welcome to CareVault. You now have full access to all features.",
    })
    
    // Wait for the success animation to play, then reload
    setTimeout(() => {
      setIsVerified(true)
      // Force a page reload to refresh user state from the server
      // This ensures the auth context gets the updated emailConfirmed status
      window.location.reload()
    }, 2000) // 2 second delay for animation
  }, [toast])

  // Handle logout from overlay
  const handleLogout = useCallback(() => {
    logout()
  }, [logout])

  // Determine if we should show the overlay
  // Use local isVerified state if set (after polling detects verification)
  // Otherwise use the user's emailConfirmed status
  const shouldShowOverlay = requireVerification && 
    user && 
    !user.emailConfirmed && 
    isVerified !== true

  // Set up verification polling when overlay is shown
  useVerificationPolling({
    enabled: shouldShowOverlay === true,
    interval: 5000, // Check every 5 seconds
    onVerified: handleVerificationComplete,
    maxRetries: 3,
  })

  if (isLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="ml-4 text-lg text-foreground">Loading...</p>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  return (
    <>
      {children}
      
      {/* Email Verification Overlay */}
      {shouldShowOverlay && (
        <EmailVerificationOverlay
          userEmail={user.email}
          onLogout={handleLogout}
          onVerificationComplete={handleVerificationComplete}
          isVerified={showSuccessAnimation}
        />
      )}
    </>
  )
}
